import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ErrorNote, FlashNote, Loading, Note, PageHead, Pill, useFlash } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime, minutes } from '../lib/format';
import { useSettings, useStaffNames } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import type { Settings } from '../lib/types';

type Key = Exclude<keyof Settings, 'updatedAt' | 'updatedByAdminId'>;
interface FieldDef {
  key: Key;
  label: string;
  hint: string;
  unit: string;
  min: number;
  max: number;
  /** shown ↔ stored */
  toShown?: (v: number) => number;
  toStored?: (v: number) => number;
  integer?: boolean;
  human?: (stored: number) => string;
}

const MONEY: FieldDef[] = [
  { key: 'commissionPercent', label: 'Default commission', hint: 'Used when no commission rule exists; saving adds a rule for every vehicle', unit: '%', min: 0, max: 50 },
  {
    key: 'riderCancellationFeePaise',
    label: 'Rider cancellation fee',
    hint: 'When a rider cancels after the free window',
    unit: '₹',
    min: 0,
    max: 500,
    toShown: (p) => p / 100,
    toStored: (r) => Math.round(r * 100),
  },
  {
    key: 'cancellationGraceSeconds',
    label: 'Free-cancel window',
    hint: 'After a driver accepts',
    unit: 'sec',
    min: 0,
    max: 3600,
    integer: true,
    human: minutes,
  },
];
const MATCHING: FieldDef[] = [
  { key: 'offerRadiusKm', label: 'Search radius', hint: 'How far from pickup drivers are asked', unit: 'km', min: 0.5, max: 30 },
  { key: 'offerBatchSize', label: 'Drivers asked at once', hint: 'Nearest first', unit: 'drivers', min: 1, max: 20, integer: true },
  { key: 'offerTimeoutSeconds', label: 'Time to accept', hint: 'Each driver’s offer', unit: 'sec', min: 5, max: 120, integer: true },
  {
    key: 'matchingTimeoutSeconds',
    label: 'Give up after',
    hint: 'Then the ride ends as “no driver found”',
    unit: 'sec',
    min: 30,
    max: 1800,
    integer: true,
    human: minutes,
  },
  {
    key: 'locationStaleSeconds',
    label: 'Driver counts as away after',
    hint: 'No location from their phone for this long',
    unit: 'sec',
    min: 15,
    max: 3600,
    integer: true,
    human: minutes,
  },
  {
    key: 'quoteTtlSeconds',
    label: 'Price shown stays bookable',
    hint: 'How long a quote lasts',
    unit: 'sec',
    min: 60,
    max: 1800,
    integer: true,
    human: minutes,
  },
];
const ALL = [...MONEY, ...MATCHING];
const shown = (f: FieldDef, s: Settings) => String(f.toShown ? f.toShown(s[f.key]) : s[f.key]);

function problem(f: FieldDef, text: string): string | null {
  const n = Number(text);
  if (text.trim() === '' || !Number.isFinite(n)) return 'Enter a number';
  if (f.integer && !Number.isInteger(n)) return 'Whole numbers only';
  if (n < f.min || n > f.max) return `Between ${f.min} and ${f.max}`;
  return null;
}

export function SettingsPage() {
  const { isOwner } = useAuth();
  const q = useSettings();
  const qc = useQueryClient();
  const [flash, show] = useFlash();
  const staffName = useStaffNames();
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.data) setValues(Object.fromEntries(ALL.map((f) => [f.key, shown(f, q.data!)])));
  }, [q.data]);

  if (q.isLoading) return <Loading rows={8} />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const s = q.data;
  const problems = Object.fromEntries(ALL.map((f) => [f.key, values[f.key] !== undefined ? problem(f, values[f.key]) : null]));
  const changed = ALL.filter((f) => values[f.key] !== undefined && values[f.key] !== shown(f, s));
  const hasProblem = ALL.some((f) => problems[f.key]);

  const renderField = (f: FieldDef) => (
    <label key={f.key} className="field">
      <span className="field-label">{f.label}</span>
      <span className="input-affix">
        {f.unit === '₹' && <span className="prefix">₹</span>}
        <input
          className={`input num ${f.unit === '₹' ? 'has-prefix' : 'has-suffix'}`}
          inputMode="decimal"
          disabled={!isOwner}
          value={values[f.key] ?? ''}
          aria-label={f.label}
          aria-invalid={!!problems[f.key]}
          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
        />
        {f.unit !== '₹' && <span className="suffix">{f.unit}</span>}
      </span>
      {problems[f.key] ? (
        <span className="field-hint bad">{problems[f.key]}</span>
      ) : (
        <span className="field-hint">
          {f.hint}
          {f.human && values[f.key] && !problems[f.key] ? ` · ${f.human(Number(values[f.key]))}` : ''}
        </span>
      )}
    </label>
  );

  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!changed.length || hasProblem) return;
        setBusy(true);
        setError(null);
        try {
          const patch = Object.fromEntries(
            changed.map((f) => {
              const n = Number(values[f.key]);
              return [f.key, f.toStored ? f.toStored(n) : n];
            }),
          );
          const next = await api.patch<Settings>('/admin/settings', patch);
          qc.setQueryData(qk.settings, next);
          show('Settings saved. They apply to new rides now.');
          void qc.invalidateQueries({ queryKey: qk.commissions });
        } catch (err) {
          setError(errorMessage(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <PageHead
        title="Settings"
        sub={
          s.updatedAt
            ? `Last changed ${dateTime(s.updatedAt)}${s.updatedByAdminId ? ` by ${staffName(s.updatedByAdminId)}` : ''}`
            : undefined
        }
        actions={!isOwner ? <Pill>View only</Pill> : undefined}
      />
      <FlashNote flash={flash} />
      {!isOwner && <Note>Only the owner can change settings.</Note>}
      {error && <Note tone="bad">{error}</Note>}
      <section className="card">
        <div className="card-head">
          <h2>Money</h2>
        </div>
        <div className="card-body stack-sm" style={{ gap: 16 }}>
          <div className="form-grid">{MONEY.map(renderField)}</div>
          <p className="small muted">
            Commission per vehicle, and changes that start later, are on the{' '}
            <Link to="/commission" className="link">
              Commission
            </Link>{' '}
            page.
          </p>
        </div>
      </section>
      <section className="card">
        <div className="card-head">
          <h2>Finding a driver</h2>
        </div>
        <div className="card-body form-grid">{MATCHING.map(renderField)}</div>
      </section>
      {isOwner && changed.length > 0 && (
        <div className="save-bar" role="region" aria-label="Unsaved changes">
          <span className="strong">
            {changed.length} change{changed.length === 1 ? '' : 's'} not saved
          </span>
          <span className="row" style={{ gap: 8 }}>
            <button type="button" className="btn" onClick={() => setValues(Object.fromEntries(ALL.map((f) => [f.key, shown(f, s)])))}>
              Undo changes
            </button>
            <button className="btn btn-dark" disabled={busy || hasProblem}>
              {busy ? 'Saving…' : `Save ${changed.length} change${changed.length === 1 ? '' : 's'}`}
            </button>
          </span>
        </div>
      )}
    </form>
  );
}
